import { memo } from "react";
import { Handle, type NodeProps } from "@xyflow/react";
import { Flag, CheckCircle, XCircle, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { OutputNodeData } from "@/types/decision-graph";
import { getHandlePositions } from "../utils/handle-positions";

interface ResultDisplay {
  icon: React.ReactNode;
  color: string;
  label: string;
}

function getResultDisplay(
  result: "YES" | "NO" | "UNKNOWN" | null | undefined
): ResultDisplay {
  switch (result) {
    case "YES":
      return {
        icon: <CheckCircle size={16} />,
        color: "text-green-600 bg-green-50",
        label: "YES",
      };
    case "NO":
      return {
        icon: <XCircle size={16} />,
        color: "text-red-600 bg-red-50",
        label: "NO",
      };
    case "UNKNOWN":
      return {
        icon: <HelpCircle size={16} />,
        color: "text-amber-600 bg-amber-50",
        label: "UNKNOWN",
      };
    default:
      return {
        icon: <Flag size={16} />,
        color: "text-slate-400 bg-slate-50",
        label: "—",
      };
  }
}

export const OutputNode = memo(function OutputNode({
  data,
  selected,
}: NodeProps) {
  const d = data as OutputNodeData & {
    previewResult?: "YES" | "NO" | "UNKNOWN" | null;
  };
  const resultDisplay = getResultDisplay(d.previewResult);
  const { inputPosition } = getHandlePositions(d.layoutDirection);

  return (
    <div
      className={cn(
        "px-4 py-3 rounded-lg border-2 bg-white shadow-sm min-w-[100px]",
        "transition-all duration-150",
        selected
          ? "border-blue-500 shadow-md ring-2 ring-blue-200"
          : "border-purple-300 hover:border-purple-400"
      )}
    >
      <Handle
        type="target"
        position={inputPosition}
        id="input"
        style={{ background: "#22c55e", width: 8, height: 8 }}
      />

      <div className="flex items-center gap-2 mb-2">
        <Flag size={14} className="text-purple-500" />
        <span className="text-xs font-semibold text-purple-700">Result</span>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <span
          className={cn(
            "px-2 py-0.5 rounded text-[10px] font-medium",
            "bg-purple-100 text-purple-700 border border-purple-200"
          )}
        >
          {d.outputType === "boolean" ? "Boolean" : "Value"}
        </span>
      </div>

      {d.outputType === "boolean" && (
        <div
          className={cn(
            "flex items-center gap-2 px-2 py-1.5 rounded-md",
            resultDisplay.color
          )}
        >
          {resultDisplay.icon}
          <span className="text-sm font-semibold">{resultDisplay.label}</span>
        </div>
      )}
    </div>
  );
});
